import React, { useEffect } from 'react';

/**
 * TasksPage Component
 * Compact single-line layout with smaller text for better data density.
 */
const TasksPage = ({
  // Data
  tasks,
  appointments,
  teamMemberAvatarUrls = {},
  // State
  taskSortBy,
  showCompletedTasks,
  futureTasksExpanded,
  activeTasksExpanded,
  // State setters
  setActiveTab,
  setSettingsTab,
  setEditingTask,
  setTaskForm,
  setShowTaskForm,
  setTaskSortBy,
  setShowCompletedTasks,
  setFutureTasksExpanded,
  setActiveTasksExpanded,
  // Functions
  getTaskAssignee,
  editTask,
  updateTaskStatus
}) => {
  const safeTasks = Array.isArray(tasks) ? tasks : [];

  // -------------------------------------------------------------------------
  // Keyboard shortcuts (scoped to the Tasks page — listener is mounted only
  // while this component is on screen)
  //   Cmd/Ctrl + T       → open the New Task form
  //   Cmd/Ctrl + Enter   → submit the open New/Edit Task form
  // -------------------------------------------------------------------------
  useEffect(() => {
    const openNewTaskForm = () => {
      setEditingTask(null);
      setTaskForm({
        appointment_id: '',
        title: '',
        description: '',
        due_date: '',
        assigned_to: '',
        priority: 'medium',
        status: 'pending',
      });
      setShowTaskForm(true);
    };

    const submitOpenTaskForm = () => {
      // Locate the Create/Update Task button inside the active task modal
      // and click it. Avoids any need to modify the modal component.
      const buttons = Array.from(document.querySelectorAll('button[type="submit"]'));
      const submitBtn = buttons.find((b) => {
        const text = (b.textContent || '').trim();
        return text === 'Create Task' || text === 'Update Task';
      });
      if (submitBtn) {
        submitBtn.click();
        return true;
      }
      return false;
    };

    const onKeyDown = (e) => {
      const isCmd = e.metaKey || e.ctrlKey;
      if (!isCmd) return;

      // Cmd/Ctrl + T → New Task
      if (
        (e.key === 't' || e.key === 'T') &&
        !e.shiftKey && !e.altKey
      ) {
        e.preventDefault();
        // Most browsers reserve Cmd+T for new tab; preventDefault wins
        // when the page has focus, which is the case while the user is
        // working in this SPA tab.
        openNewTaskForm();
        return;
      }

      // Cmd/Ctrl + Enter → submit the currently open task form
      if (e.key === 'Enter') {
        if (submitOpenTaskForm()) {
          e.preventDefault();
        }
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [setEditingTask, setTaskForm, setShowTaskForm]);

  // Sort tasks
  const sortTasks = (tasksToSort) => {
    return [...tasksToSort].sort((a, b) => {
      switch (taskSortBy) {
        case 'due_date':
          return new Date(a.due_date) - new Date(b.due_date);
        case 'priority':
          const priorityOrder = { high: 0, medium: 1, low: 2 };
          return priorityOrder[a.priority] - priorityOrder[b.priority];
        case 'status':
          return a.status.localeCompare(b.status);
        case 'assigned_to':
          return (a.assigned_to || '').localeCompare(b.assigned_to || '');
        default:
          return 0;
      }
    });
  };

  // Handle checkbox toggle for task status
  const handleCheckboxToggle = (e, task) => {
    e.stopPropagation(); // Prevent row click from triggering
    const newStatus = task.status === 'completed' ? 'pending' : 'completed';
    updateTaskStatus(task.id, newStatus);
  };

  // Compact task row component
  // Parse a "YYYY-MM-DD" string as a LOCAL date (no timezone shift).
  //   `new Date("2026-06-15")` is interpreted as UTC midnight, then rendered
  //   in the user's local timezone — west of UTC it shows the day before.
  //   We build the Date from individual year/month/day parts so it always
  //   stays on the date the user actually picked. Any other format (ISO with
  //   time) is left to the default parser.
  const parseLocalDate = (s) => {
    if (!s) return null;
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(s).trim());
    if (m) return new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
    return new Date(s);
  };

  const TaskRow = ({ task, isOverdue = false }) => {
    const appointment = appointments.find(apt => apt.id === task.appointment_id);
    const assignee = getTaskAssignee(task.assigned_to);
    const isCompleted = task.status === 'completed';
    const dueDate = task.due_date ? parseLocalDate(task.due_date) : null;
    
    return (
      <tr 
        className={`hover:bg-gray-50 transition-colors ${isOverdue ? 'bg-red-50/50' : ''}`}
        onClick={() => editTask(task)}
        style={{ cursor: 'pointer' }}
        data-testid={`task-row-${task.id}`}
      >
        {/* Checkbox for marking Done */}
        <td className="pl-4 pr-2 py-2">
          <input
            type="checkbox"
            checked={isCompleted}
            onChange={(e) => handleCheckboxToggle(e, task)}
            onClick={(e) => e.stopPropagation()}
            className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
            data-testid={`task-checkbox-${task.id}`}
          />
        </td>
        
        {/* Task Title */}
        <td className="px-3 py-2 max-w-[220px]">
          <div className="flex items-center gap-2">
            <span className={`text-sm font-medium truncate ${isCompleted ? 'text-gray-400 line-through' : 'text-gray-900'}`}>{task.title}</span>
            {task.is_automated && (
              <span className="flex-shrink-0 px-1.5 py-0.5 text-xs bg-violet-100 text-violet-700 rounded">A</span>
            )}
            {task.description && task.description.trim() !== '' && (
              <span
                className="flex-shrink-0 inline-flex items-center text-gray-400 hover:text-gray-600"
                title="This task has notes"
                aria-label="Has notes"
                data-testid={`task-notes-icon-${task.id}`}
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h4m4 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </span>
            )}
          </div>
        </td>
        
        {/* Property Address + Client (stacked) */}
        <td className="px-3 py-2 max-w-[240px]">
          <div className={`text-sm truncate ${isCompleted ? 'text-gray-400' : 'text-gray-900'}`}>{appointment?.property_address || '—'}</div>
          <div className="text-xs text-gray-400 truncate">{appointment?.client_name || ''}</div>
        </td>
        
        {/* Due Date - Calendar style */}
        <td className="px-3 py-2">
          {dueDate ? (
            <div className={`w-12 text-center rounded-lg py-1 ${
              isOverdue && !isCompleted 
                ? 'bg-red-100' 
                : isCompleted 
                  ? 'bg-gray-100' 
                  : 'bg-blue-50'
            }`}>
              <p className={`text-xs uppercase ${
                isOverdue && !isCompleted 
                  ? 'text-red-600' 
                  : isCompleted 
                    ? 'text-gray-400' 
                    : 'text-blue-600'
              }`}>
                {dueDate.toLocaleDateString('en-US', { month: 'short' })}
              </p>
              <p className={`text-lg font-bold ${
                isOverdue && !isCompleted 
                  ? 'text-red-700' 
                  : isCompleted 
                    ? 'text-gray-400' 
                    : 'text-blue-700'
              }`}>
                {dueDate.getDate()}
              </p>
            </div>
          ) : (
            <span className="text-sm text-gray-400">—</span>
          )}
        </td>
        
        {/* Assignee - Avatar only, name on hover */}
        <td className="px-3 py-2">
          {assignee ? (
            <div className="relative group inline-block">
              {assignee.avatar && teamMemberAvatarUrls[assignee.id] ? (
                <img src={teamMemberAvatarUrls[assignee.id]} alt="" className="w-8 h-8 rounded-full object-cover" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-gray-400 flex items-center justify-center text-white text-xs font-medium">
                  {assignee.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                </div>
              )}
              {/* Tooltip on hover */}
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 px-2 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                {assignee.name}
              </div>
            </div>
          ) : (
            <span className="text-sm text-gray-400">—</span>
          )}
        </td>
        
        {/* Priority */}
        <td className="px-3 py-2">
          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded ${
            task.priority === 'high' ? 'bg-red-100 text-red-700' :
            task.priority === 'medium' ? 'bg-amber-100 text-amber-700' :
            'bg-green-100 text-green-700'
          }`}>
            {task.priority.charAt(0).toUpperCase() + task.priority.slice(1)}
          </span>
        </td>
        
        {/* Status */}
        <td className="px-3 py-2">
          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded ${
            task.status === 'pending' ? 'bg-amber-100 text-amber-700' :
            task.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
            'bg-emerald-100 text-emerald-700'
          }`}>
            {task.status === 'in_progress' ? 'In Progress' : task.status === 'pending' ? 'Pending' : 'Done'}
          </span>
        </td>
      </tr>
    );
  };

  // Table header component
  const TableHeader = () => (
    <thead>
      <tr className="border-b border-gray-200">
        <th className="pl-4 pr-2 py-3 w-10">
          <span className="sr-only">Done</span>
        </th>
        <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Task</th>
        <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Property</th>
        <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Due</th>
        <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Assigned</th>
        <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Priority</th>
        <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
      </tr>
    </thead>
  );

  // Get task counts
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const futureAutomatedTasks = safeTasks.filter(task => {
    if (!task.is_automated || task.status === 'completed') return false;
    const appointment = appointments.find(apt => apt.id === task.appointment_id);
    if (!appointment) return false;
    const appointmentDate = new Date(appointment.scheduled_date);
    appointmentDate.setHours(0, 0, 0, 0);
    return appointmentDate > today;
  });

  const activeTasks = safeTasks.filter(task => {
    if (task.status === 'completed') return false;
    if (task.is_automated) {
      const appointment = appointments.find(apt => apt.id === task.appointment_id);
      if (appointment) {
        const appointmentDate = new Date(appointment.scheduled_date);
        appointmentDate.setHours(0, 0, 0, 0);
        if (appointmentDate > today) return false;
      }
    }
    return true;
  });

  const completedTasks = safeTasks.filter(task => task.status === 'completed');

  return (
    <div className="space-y-6 max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Tasks</h2>
        <div className="flex items-center gap-3">
          {/* Sort Control */}
          <select
            value={taskSortBy}
            onChange={(e) => setTaskSortBy(e.target.value)}
            className="text-sm px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            data-testid="task-sort-select"
          >
            <option value="due_date">Sort: Due Date</option>
            <option value="priority">Sort: Priority</option>
            <option value="status">Sort: Status</option>
            <option value="assigned_to">Sort: Assignee</option>
          </select>
          
          <button
            onClick={() => {
              setActiveTab('settings');
              setSettingsTab('tasks');
            }}
            className="text-sm px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
            data-testid="automated-tasks-btn"
          >
            ⚙️ Automated Tasks
          </button>
          
          <button
            onClick={() => {
              setEditingTask(null);
              setTaskForm({ appointment_id: '', title: '', description: '', due_date: '', assigned_to: '', priority: 'medium', status: 'pending' });
              setShowTaskForm(true);
            }}
            className="text-sm px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            data-testid="add-task-btn"
          >
            + Add Task
          </button>
        </div>
      </div>

      {/* Future Tasks Section */}
      {futureAutomatedTasks.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
          <button 
            className="w-full px-4 py-3 bg-violet-50 hover:bg-violet-100 border-b border-violet-200 flex items-center justify-between transition-colors"
            onClick={() => setFutureTasksExpanded(!futureTasksExpanded)}
          >
            <div className="flex items-center gap-3">
              <svg className={`w-4 h-4 text-violet-600 transition-transform ${futureTasksExpanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              <span className="text-sm font-semibold text-violet-800">FUTURE TASKS</span>
              <span className="text-xs text-violet-500">(Activate on shoot day)</span>
            </div>
            <span className="px-2 py-1 text-xs font-medium bg-violet-200 text-violet-700 rounded-full">
              {futureAutomatedTasks.length}
            </span>
          </button>
          {futureTasksExpanded && (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <TableHeader />
                <tbody className="divide-y divide-gray-100">
                  {futureAutomatedTasks.map(task => (
                    <TaskRow key={task.id} task={task} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Active Tasks Section */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        <button 
          className="w-full px-4 py-3 bg-sky-50 hover:bg-sky-100 border-b border-sky-200 flex items-center justify-between transition-colors"
          onClick={() => setActiveTasksExpanded(!activeTasksExpanded)}
        >
          <div className="flex items-center gap-3">
            <svg className={`w-4 h-4 text-sky-600 transition-transform ${activeTasksExpanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <span className="text-sm font-semibold text-sky-800">ACTIVE TASKS</span>
          </div>
          <span className="px-2 py-1 text-xs font-medium bg-sky-200 text-sky-700 rounded-full">
            {activeTasks.length}
          </span>
        </button>
        {activeTasksExpanded && (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <TableHeader />
              <tbody className="divide-y divide-gray-100">
                {sortTasks(activeTasks).map(task => {
                  const isOverdue = task.due_date && new Date(task.due_date) < new Date();
                  return <TaskRow key={task.id} task={task} isOverdue={isOverdue} />;
                })}
                {activeTasks.length === 0 && (
                  <tr>
                    <td colSpan="7" className="px-4 py-8 text-center text-sm text-gray-500">
                      No active tasks
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Completed Tasks Section */}
      {completedTasks.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
          <button 
            className="w-full px-4 py-3 bg-emerald-50 hover:bg-emerald-100 border-b border-emerald-200 flex items-center justify-between transition-colors"
            onClick={() => setShowCompletedTasks(!showCompletedTasks)}
          >
            <div className="flex items-center gap-3">
              <svg className={`w-4 h-4 text-emerald-600 transition-transform ${showCompletedTasks ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              <span className="text-sm font-semibold text-emerald-800">COMPLETED</span>
            </div>
            <span className="px-2 py-1 text-xs font-medium bg-emerald-200 text-emerald-700 rounded-full">
              {completedTasks.length}
            </span>
          </button>
          {showCompletedTasks && (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <TableHeader />
                <tbody className="divide-y divide-gray-100">
                  {completedTasks.map(task => (
                    <TaskRow key={task.id} task={task} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TasksPage;
